<template>
  <div class="mt-4">
    <p class="text-caption text-grey-body">
      See what each <a href="/background-checks/whats-covered" target="_blank" class="text-primary underline">badge covers</a> and
      learn more about our commitment to safety by visiting the
      <a href="/trust-and-safety" target="_blank" class="text-primary underline">Trust & Safety center</a>.
    </p>

    <div v-for="(badge, index) in visibleBadges" :key="index" class="flex gap-4 mt-16">
      <component :is="badge.graphic" :width="60" :height="60" :active="badge.active" />
      <help-icon v-if="badge.flagged" class="absolute left-60 md-1:left-68" color="#282930" :aria-hidden="true" />
      <div>
        <p class="text-strong-body text-grey-header">{{ badge.title }}</p>
        <p class="flex flex-col text-caption" :class="getBadgeTextClasses(badge)">
          <span v-if="badge.completedAtText">{{ badge.completedAtText }}</span>
          <span v-if="badge.text">{{ badge.text }}</span>
        </p>
        <poppins-button
          v-if="badge.manageable"
          value="Get a copy"
          href=" https://candidate.checkr.com/login#login"
          variant="text"
        />
      </div>
    </div>

    <poppins-button :value="btnData.value" :href="btnData.href" variant="outlined" size="medium" class="mt-16 w-full" />
    <p v-if="!profileBelongsToViewer" class="text-caption text-grey-body mt-16">
      We encourage parents to conduct their own screenings including using the background check tools.
    </p>
  </div>
</template>

<script lang="ts">
import PoppinsButton from '@/components/button/Button.vue';
import IDVBadge from '@/graphics/badges/IDVBadge.vue';
import BackgroundCheckBadge from '@/graphics/badges/BackgroundCheckBadge.vue';
import EnhancedBackgroundCheckBadge from '@/graphics/badges/EnhancedBackgroundCheckBadge.vue';
import MotorVehicleRecordBadge from '@/graphics/badges/MotorVehicleRecordBadge.vue';
import HelpIcon from '@/graphics/icons/HelpIcon.vue';

interface Badge {
  completedAt: Date;
  expiresAt: Date;
  name: string;
  status: string;
}

interface IDV {
  latestUpdate: Date;
  provider: string;
  status: string;
  verdict: string;
}

interface BadgeDisplay {
  key: string;
  graphic: any;
  title: string;
  text: string;
  completedAtText?: string;
  active?: boolean;
  manageable?: boolean;
  flagged?: boolean;
}

export default {
  name: 'ProfileTrustAndSafety',
  components: {
    PoppinsButton,
    HelpIcon,
  },
  props: {
    badges: {
      type: Array as () => Badge[],
      default: () => [],
    },
    idv: {
      type: Object as () => IDV,
      default: () => ({}),
    },
    viewerAccountData: {
      type: Object,
      default: () => ({}),
    },
    profileBelongsToViewer: {
      type: Boolean,
      default: false,
    },
    sitterId: {
      type: String,
      default: '',
    },
  },
  computed: {
    btnData(): { value: string; href: string } {
      return !this.viewerLoggedIn
        ? { value: 'Log in', href: `/login?return_path=${encodeURIComponent(window.location.pathname)}` }
        : {
            value: 'Run new check',
            href: this.profileBelongsToViewer
              ? '/account/providers/background-checks'
              : `/background-check/sponsored-checks/${this.sitterId}/new`,
          };
    },
    identityVerificationPassed(): boolean {
      return this.idv?.verdict === 'passed';
    },
    viewerLoggedIn(): boolean {
      return this.viewerAccountData !== null;
    },
    visibleBadges(): BadgeDisplay[] {
      const badgeConfig = [
        {
          key: 'idv',
          graphic: IDVBadge,
          title: 'Identity verified',
          text: 'Completed document verification.',
          completedAtText: '',
        },
        {
          key: 'backgroundCheck',
          graphic: BackgroundCheckBadge,
          title: 'Basic criminal check',
          ...this.getBadgeDisplayData('basic_criminal'),
        },
        {
          key: 'enhancedBackgroundCheck',
          graphic: EnhancedBackgroundCheckBadge,
          title: 'Enhanced criminal check',
          ...this.getBadgeDisplayData('enhanced_criminal'),
        },
        {
          key: 'motorVehicleRecord',
          graphic: MotorVehicleRecordBadge,
          title: 'Motor vehicle records',
          ...this.getBadgeDisplayData('motor_vehicle'),
        },
      ];

      // Filter out IDV badge if not passed & hide badge details if viewer is not logged in
      return badgeConfig
        .filter((badge) => badge.key !== 'idv' || (badge.key === 'idv' && this.identityVerificationPassed))
        .map((badge) => ({
          ...badge,
          completedAtText: this.viewerLoggedIn ? badge.completedAtText : '',
          text: this.viewerLoggedIn ? badge.text : 'Log in to view details',
        }));
    },
  },
  methods: {
    getBadgeDisplayData(badgeName: string): object {
      const badge = this.badges?.find((b) => b.name === badgeName);

      const badgeDetails = {
        status: badge?.status || '',
        completedDate: badge?.completedAt ? new Date(badge.completedAt).toLocaleDateString() : '',
        expirationDate: badge?.expiresAt ? new Date(badge.expiresAt).toLocaleDateString() : '',
      };

      const completedAtText = `Completed ${badgeDetails.completedDate}.`;

      switch (badgeDetails.status) {
        case 'PENDING':
          return {
            text: 'Pending. Check for results soon.',
            active: false,
          };
        case 'COMPLETE':
          return {
            completedAtText,
            text: this.profileBelongsToViewer
              ? 'Parents may ask to see a copy.'
              : 'Contact the caregiver directly to see the report.',
            active: true,
            manageable: this.profileBelongsToViewer,
          };
        case 'REVIEW_RECOMMENDED':
          return {
            completedAtText,
            text: this.profileBelongsToViewer
              ? 'Eligible for badge with information review. Parents may ask to see a copy.'
              : 'Badge has been awarded, but we recommend contacting the sitter to see results.',
            active: true,
            flagged: true,
            manageable: this.profileBelongsToViewer,
          };
        case 'INELIGIBLE':
          return {
            completedAtText,
            text: this.profileBelongsToViewer
              ? 'This check was completed, and you did not meet the requirements to receive the badge.'
              : 'This check was completed, and the caregiver did not meet requirements to receive the badge.',
            active: false,
            ineligible: true,
            manageable: this.profileBelongsToViewer,
          };
        case 'EXPIRED':
          return {
            text: `Expired on ${badgeDetails.expirationDate}.`,
            active: false,
            manageable: this.profileBelongsToViewer,
          };
        default:
          return {
            text: 'Never run.',
            active: false,
          };
      }
    },
    getBadgeTextClasses(badge): string {
      if (!this.viewerLoggedIn) return 'text-grey-body italic';
      return badge.ineligible ? 'text-critical-contrast' : 'text-grey-body';
    },
  },
};
</script>
